'use client'

import { useCallback, useMemo } from 'react'

import { AnchorLink } from '@graftcode/design-system'

import { CONTENT_NAV_TEST_IDS } from '@/shared/docs-test-ids.const'

import { useActiveHeading } from './hooks'
import { extractHeadings } from './utils'

import styles from './ContentNav.module.scss'

interface ContentNavProps {
  content: string
}

export function ContentNav({ content }: ContentNavProps) {
  const headings = useMemo(() => extractHeadings(content), [content])
  const { activeId, scrollToHeading } = useActiveHeading(headings)

  const handleClick = useCallback(
    (headingId: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()
      scrollToHeading(headingId)
    },
    [scrollToHeading]
  )

  if (headings.length === 0) return null

  return (
    <nav data-testid={CONTENT_NAV_TEST_IDS.root} className={styles.nav}>
      <span className={styles.navTitle}>On this page</span>
      <div className={styles.linksContainer}>
        <div className={styles.inArticleNav}>
          <div className={styles.lineContainer}>
            <div className={styles.line} />
          </div>
          <div className={styles.navItems}>
            {headings.map((heading) => {
              const isActive = heading.id === activeId
              const isNested = heading.level > 2

              return (
                <AnchorLink
                  key={heading.id}
                  href={`#${heading.id}`}
                  isActive={isActive}
                  className={`${styles.navItem} ${isActive ? styles.active : ''} ${isNested ? styles.nested : ''}`}
                  onClick={handleClick(heading.id)}
                >
                  <span className={styles.dotContainer}>{isActive && <span className={styles.dot} />}</span>
                  <span className={styles.navLabel}>{heading.text}</span>
                </AnchorLink>
              )
            })}
          </div>
        </div>
      </div>
    </nav>
  )
}
